import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Disclaimer } from "../components/Disclaimer";
import { toast } from "sonner";
import { BRAND_IMG } from "../components/SiteBackground";

export default function Welcome() {
  const { login, register, guest } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "register") await register(email, password);
      else await login(email, password);
      nav("/home");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const asGuest = async () => {
    setBusy(true);
    try { await guest(); nav("/scan"); }
    catch { toast.error("Could not start guest session"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 relative" style={{ background: "#FFFFFF" }}>
      <div className="hidden md:flex flex-col justify-center items-center p-10 relative z-10" style={{ background: "#FFFFFF", borderRight: "1px solid var(--border-subtle)" }}>
        <img src={BRAND_IMG} alt="Path Mentor" className="w-full max-w-lg object-contain" data-testid="brand-image" />
        <p className="mt-6 text-center leading-relaxed text-sm max-w-md" style={{ color: "var(--text-2)" }}>
          An AI mentor for everyday people. Point your camera at a thing, measure it, or answer a few
          questions — and get local ideas, honest cost-vs-earn math, and this week's first step. Works even at $0.
        </p>
        <ul className="mt-5 space-y-1.5 font-mono text-xs" style={{ color: "var(--text-2)" }}>
          <li>— No stock picking. No hype. No promised income.</li>
          <li>— Tailored to where you actually are.</li>
          <li>— We tell you when something is a waste.</li>
        </ul>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-10 max-w-md w-full mx-auto relative z-10">
        <div className="md:hidden mb-6"><img src={BRAND_IMG} alt="Path Mentor" className="w-full max-w-xs mx-auto object-contain" /></div>
        <h2 className="font-head font-bold text-2xl mb-1">{mode === "register" ? "Create your account" : "Welcome back"}</h2>
        <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
          {mode === "register" ? "Start free. Save your path and download it anytime." : "Sign in to continue your path."}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input className="pm-input" type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email" />
          <input className="pm-input" type="password" placeholder="Password (6+ chars)" value={password}
            onChange={(e) => setPassword(e.target.value)} required data-testid="auth-password" />
          {error && <p className="text-sm" style={{ color: "var(--warning)" }} data-testid="auth-error">{error}</p>}
          <button className="pm-btn w-full" disabled={busy} data-testid="auth-submit">
            {busy ? "..." : mode === "register" ? "Create account" : "Sign in"}
          </button>
        </form>
        <button className="text-sm mt-3 underline" data-testid="auth-toggle"
          onClick={() => setMode(mode === "register" ? "login" : "register")}>
          {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>
        <div className="my-5 label-mono text-center">or</div>
        <button className="pm-btn-ghost w-full flex items-center justify-center gap-2" onClick={googleSignIn} disabled={busy} data-testid="google-signin-btn">
          <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
        </button>
        <button className="pm-btn-ghost w-full mt-3" onClick={asGuest} disabled={busy} data-testid="guest-btn">
          Try as guest (one scan or measure)
        </button>
        <div className="mt-6"><Disclaimer compact /></div>
        <div className="mt-4 flex items-center gap-4 label-mono">
          <a href="/privacy" className="underline" data-testid="welcome-privacy-link">Privacy Policy</a>
          <a href="mailto:pathmentorai@gmail.com?subject=Path%20Mentor%20Support" className="underline" data-testid="welcome-support-link">Contact support</a>
          <a href="tel:+18575203500" className="underline" data-testid="welcome-support-phone">(857) 520-3500</a>
        </div>
      </div>
    </div>
  );
}
